/**
 * Cross-browser photo print (Chrome / Edge / Firefox / Safari / iOS Safari).
 *
 * Safari ignores @page size, `position:fixed`, `inset`, and often `object-fit`
 * in the print pipeline, which shows as white bars. This path:
 * 1. Cover-crops onto an A4 canvas when CORS allows (no object-fit needed)
 * 2. Prints via a hidden iframe on Apple WebKit (popup print is unreliable)
 * 3. Uses background-size:cover + -webkit-print-color-adjust as a CSS fallback
 * 4. Waits for decode + afterprint so Safari does not print a blank/partial page
 */
function isAppleWebKit() {
  const ua = navigator.userAgent || "";
  const vendor = navigator.vendor || "";
  if (/iPhone|iPad|iPod/i.test(ua)) return true;
  if (vendor.indexOf("Apple") !== -1 && /Safari/i.test(ua) && !/Chrome|CriOS|FxiOS|EdgiOS/i.test(ua)) {
    return true;
  }
  return false;
}

function escapeHtml(title: string) {
  return String(title || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** A4 @ ~200dpi so the bitmap already matches default macOS/Safari paper. */
function renderCoverJpeg(img: HTMLImageElement, landscape: boolean): string {
  const longEdge = 2338;
  const shortEdge = 1654;
  const cw = landscape ? longEdge : shortEdge;
  const ch = landscape ? shortEdge : longEdge;
  const canvas = document.createElement("canvas");
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas");
  const scale = Math.max(cw / img.naturalWidth, ch / img.naturalHeight);
  const dw = img.naturalWidth * scale;
  const dh = img.naturalHeight * scale;
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, cw, ch);
  ctx.drawImage(img, (cw - dw) / 2, (ch - dh) / 2, dw, dh);
  return canvas.toDataURL("image/jpeg", 0.92);
}

function buildPrintHtml(src: string, title: string, landscape: boolean) {
  const page = landscape ? "landscape" : "portrait";
  return `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${title}</title>
    <style>
      @page { margin: 0; size: ${page}; }
      html, body {
        margin: 0 !important;
        padding: 0 !important;
        width: 100%;
        height: 100%;
        background-color: #000;
        background-image: url("${src}");
        background-repeat: no-repeat;
        background-position: center center;
        background-size: cover;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
        color-adjust: exact;
      }
      img {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 100%;
        max-width: none;
        max-height: none;
        object-fit: cover;
        object-position: center;
        display: block;
        border: 0;
      }
      @media print {
        html, body {
          width: 100%;
          height: 100%;
          margin: 0 !important;
          padding: 0 !important;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        img {
          width: 100% !important;
          height: 100% !important;
        }
      }
    </style>
  </head>
  <body>
    <img id="print-photo" src="${src}" alt="${title}" />
  </body>
</html>`;
}

function openPrintTarget(apple: boolean): { win: Window; doc: Document; cleanup: () => void } | null {
  if (apple) {
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.setAttribute("title", "print");
    iframe.style.cssText =
      "position:absolute;width:800px;height:600px;left:-99999px;top:0;border:0;opacity:0;";
    document.body.appendChild(iframe);
    const win = iframe.contentWindow;
    const doc = iframe.contentDocument || win?.document;
    if (!win || !doc) {
      iframe.remove();
      return null;
    }
    return {
      win,
      doc,
      cleanup: () => {
        setTimeout(() => iframe.remove(), 0);
      },
    };
  }

  const win = window.open("", "_blank");
  if (!win) return openPrintTarget(true);
  return {
    win,
    doc: win.document,
    cleanup: () => {
      try {
        win.close();
      } catch {
        // Safari may already have closed the window after print.
      }
    },
  };
}

function printDocument(src: string, title: string, landscape: boolean) {
  const apple = isAppleWebKit();
  const target = openPrintTarget(apple);
  if (!target) return;
  const { win, doc, cleanup } = target;

  doc.open();
  doc.write(buildPrintHtml(src, title, landscape));
  doc.close();

  const img = doc.getElementById("print-photo") as HTMLImageElement | null;

  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    cleanup();
  };

  let printed = false;
  const runPrint = () => {
    if (printed) return;
    printed = true;
    try {
      win.focus();
    } catch {
      // ignore
    }
    win.print();
    if (!apple) {
      finish();
      return;
    }
    win.addEventListener("afterprint", finish);
    setTimeout(finish, 120000);
  };

  const schedulePrint = () => {
    const delay = apple ? 300 : 80;
    const start = () => {
      if (typeof win.requestAnimationFrame === "function") {
        win.requestAnimationFrame(() => setTimeout(runPrint, delay));
      } else {
        setTimeout(runPrint, delay);
      }
    };
    if (img && typeof img.decode === "function") {
      img.decode().then(start).catch(start);
    } else {
      start();
    }
  };

  if (!img) {
    schedulePrint();
    return;
  }
  if (img.complete && img.naturalWidth > 0) {
    schedulePrint();
    return;
  }
  img.onload = schedulePrint;
  img.onerror = schedulePrint;
}

function loadImage(url: string, withCors: boolean): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (withCors) img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("image load failed"));
    img.src = url;
  });
}

export function printPhoto(imageUrl: string, title: string) {
  if (!imageUrl) return;
  const safeTitle = escapeHtml(title);

  loadImage(imageUrl, true)
    .then((img) => {
      const landscape = img.naturalWidth >= img.naturalHeight;
      try {
        printDocument(renderCoverJpeg(img, landscape), safeTitle, landscape);
      } catch {
        printDocument(imageUrl, safeTitle, landscape);
      }
    })
    .catch(() => {
      loadImage(imageUrl, false)
        .then((img) => {
          printDocument(imageUrl, safeTitle, img.naturalWidth >= img.naturalHeight);
        })
        .catch(() => {
          printDocument(imageUrl, safeTitle, true);
        });
    });
}
